import React, {
    useCallback,
    useEffect,
    useMemo,
    useState
} from "react";
import { Link } from "react-router-dom";
import Collapse from "react-bootstrap/Collapse";
import Select from "react-select";
import Swal from "sweetalert2";
import PassbookDetailsModal from "./PassbookDetailsModal";
import CashPassbookEntriesModal from "./CashPassbookEntriesModal";

import CashbookAddModal from "./CashbookAddModal";
import Pagination from "../Pagenation/Pagenation";

import CashbookServices from "../../../services/CashbookServices";
import LocationServices from "../../../services/LocationServices";

const readData = (response) => {
    const body = response?.data?.status
        ? response.data
        : response;

    return body?.data;
};

const rowOptions = [5, 10, 20, 50, 100].map((value) => ({
    value,
    label: String(value)
}));

const formatCashAmount = (value) => {
    if (value === null || value === undefined || value === "") {
        return "—";
    }

    const amount = Number(value);

    return Number.isFinite(amount)
        ? amount.toLocaleString("en-IN", {
            style: "currency",
            currency: "INR",
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
        })
        : "—";
};

const CashbookManagement = () => {
    const [filterOpen, setFilterOpen] = useState(false);
    const [listOpen, setListOpen] = useState(true);
    const [activeTab, setActiveTab] = useState("active");
    const [showAddModal, setShowAddModal] = useState(false);
    const [viewPassbook, setViewPassbook] = useState(null);
    const [stores, setStores] = useState([]);
    const [cashbooks, setCashbooks] = useState([]);
    const [loading, setLoading] = useState(true);
    const [storesLoading, setStoresLoading] = useState(true);
    const [selectedPassbook, setSelectedPassbook] = useState(null);
    const [storesError, setStoresError] = useState("");
    const [error, setError] = useState("");
    const [changingId, setChangingId] = useState("");

    const [searchTerm, setSearchTerm] = useState("");
    const [storeFilter, setStoreFilter] = useState("");
    const [balanceFilter, setBalanceFilter] = useState("");

    const balanceOptions = [
        { value: "", label: "All Balances" },
        { value: "positive", label: "Positive Balance" },
        { value: "negative", label: "Negative Balance" },
        { value: "zero", label: "Zero Balance" }
    ];
    const [pageSize, setPageSize] = useState(100);
    const [page, setPage] = useState(0);

    const fetchCashbooks = useCallback(async () => {
        setLoading(true);
        setError("");

        try {
            const response = await CashbookServices.getPassbooks();
            const data = readData(response);

            if (!Array.isArray(data?.passbooks)) {
                throw new Error("Unexpected cashbook API response.");
            }

            setCashbooks(data.passbooks);
        } catch (err) {
            const message =
                err?.response?.data?.message ||
                err?.message ||
                "Unable to load cashbooks.";

            setError(message);

            await Swal.fire({
                icon: "error",
                title: "Unable to Load Cashbooks",
                text: message,
                confirmButtonText: "OK"
            });
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchCashbooks();
    }, [fetchCashbooks]);

    useEffect(() => {
        let cancelled = false;

        const loadStores = async () => {
            setStoresLoading(true);
            setStoresError("");

            try {
                const response = await LocationServices.getStore();
                const allStores = response?.data?.data;

                if (!Array.isArray(allStores)) {
                    throw new Error("Unexpected store API response.");
                }

                if (!cancelled) {
                    setStores(allStores);
                }
            } catch (err) {
                if (!cancelled) {
                    const message =
                        err?.response?.data?.message ||
                        err?.message ||
                        "Unable to load stores.";

                    setStoresError(message);

                    Swal.fire(
                        "Unable to Load Stores",
                        message,
                        "error"
                    );
                }
            } finally {
                if (!cancelled) setStoresLoading(false);
            }
        };

        loadStores();

        return () => {
            cancelled = true;
        };
    }, []);

    const storeOptions = useMemo(() => {
        // Store directory is used only for missing details.
        const storeDirectory = new Map(
            stores.map((store) => [String(store._id), store])
        );

        const availableStores = new Map();

        // Include only stores linked to an existing passbook.
        // Keep options independent of search, balance and pagination.
        cashbooks.forEach((cashbook) => {
            const linkedStore = cashbook.store;

            const storeId =
                typeof linkedStore === "string"
                    ? linkedStore
                    : linkedStore?._id;

            if (!storeId) return;

            const id = String(storeId);
            const directoryStore = storeDirectory.get(id);

            const populatedStore =
                linkedStore && typeof linkedStore === "object"
                    ? linkedStore
                    : {};

            const previous = availableStores.get(id);

            availableStores.set(id, {
                _id: id,
                name:
                    populatedStore.name ||
                    directoryStore?.name ||
                    previous?.name ||
                    "",
                location:
                    populatedStore.location ||
                    directoryStore?.location ||
                    previous?.location ||
                    "",
                storeCode:
                    populatedStore.storeCode ||
                    directoryStore?.storeCode ||
                    previous?.storeCode ||
                    "",
                count: (previous?.count || 0) + 1
            });
        });

        return [
            { value: "", label: "All Stores with Passbooks" },
            ...Array.from(availableStores.values())
                .sort((a, b) =>
                    String(a.name || a._id).localeCompare(
                        String(b.name || b._id),
                        "en",
                        { numeric: true, sensitivity: "base" }
                    )
                )
                .map((store) => ({
                    value: store._id,
                    label: [
                        store.name || `Store ${store._id}`,
                        store.location,
                        store.storeCode
                    ]
                        .filter(Boolean)
                        .join(" — "),
                    count: store.count
                }))
        ];
    }, [stores, cashbooks]);

    const filteredCashbooks = useMemo(() => {
        const search = searchTerm.trim().toLowerCase();

        return cashbooks.filter((cashbook) => {
            // Active / inactive tab.
            if (cashbook.status !== activeTab) return false;

            // Store filter.
            const storeId = String(
                cashbook.store?._id || cashbook.store || ""
            );

            if (storeFilter && storeId !== String(storeFilter)) {
                return false;
            }

            // Balance filter.
            if (balanceFilter) {
                const rawBalance = cashbook.balance;

                if (
                    rawBalance === null ||
                    rawBalance === undefined ||
                    rawBalance === ""
                ) {
                    return false;
                }

                const balance = Number(rawBalance);

                if (!Number.isFinite(balance)) return false;

                if (balanceFilter === "positive" && balance <= 0) {
                    return false;
                }

                if (balanceFilter === "negative" && balance >= 0) {
                    return false;
                }

                if (balanceFilter === "zero" && balance !== 0) {
                    return false;
                }
            }

            // Existing text search, including passbook name.
            const searchableText = [
                cashbook.name,
                cashbook.employee?.name,
                cashbook.employee?.email,
                cashbook.employee?.mobileNo,
                cashbook.store?.name,
                cashbook.store?.location,
                cashbook.store?.storeCode,
                cashbook.remark
            ]
                .filter((value) => value !== null && value !== undefined)
                .join(" ")
                .toLowerCase();

            return !search || searchableText.includes(search);
        });
    }, [
        cashbooks,
        activeTab,
        searchTerm,
        storeFilter,
        balanceFilter
    ]);

    const totalPages = Math.max(
        1,
        Math.ceil(filteredCashbooks.length / pageSize)
    );

    const currentPage = Math.min(
        Math.max(0, page),
        totalPages - 1
    );

    const start = currentPage * pageSize;

    const visibleCashbooks = filteredCashbooks.slice(
        start,
        start + pageSize
    );

    useEffect(() => {
        setPage(0);
    }, [
        activeTab,
        searchTerm,
        storeFilter,
        balanceFilter,
        pageSize
    ]);

    const handlePageChange = (nextPage) => {
        const pageNumber = Number(nextPage);

        if (!Number.isInteger(pageNumber)) return;

        setPage(
            Math.min(
                Math.max(0, pageNumber),
                totalPages - 1
            )
        );
    };

    const toggleStatus = async (cashbook) => {
        if (changingId || loading) return;

        const nextStatus =
            cashbook.status === "active" ? "inactive" : "active";

        const result = await Swal.fire({
            title: `Mark cashbook ${nextStatus}?`,
            text: `${cashbook.employee?.name || "Employee"} — ${cashbook.store?.name || "Store"
                }`,
            icon: "question",
            showCancelButton: true,
            confirmButtonText:
                nextStatus === "active" ? "Activate" : "Deactivate"
        });

        if (!result.isConfirmed) return;

        try {
            setChangingId(cashbook._id);

            await CashbookServices.updatePassbookStatus(
                cashbook._id,
                nextStatus
            );

            await fetchCashbooks();
        } catch (err) {
            await Swal.fire(
                "Error",
                err?.response?.data?.message ||
                "Unable to update cashbook status.",
                "error"
            );
        } finally {
            setChangingId("");
        }
    };

    const handleCreated = async () => {
        setShowAddModal(false);
        setActiveTab("active");
        setSearchTerm("");
        setStoreFilter("");
        setBalanceFilter("");
        setPage(0);
        setListOpen(true);

        await fetchCashbooks();
    };

    const tabStyle = (tab) => ({
        border: "none",
        padding: "8px 18px",
        borderRadius: "8px",
        background:
            activeTab === tab ? "#0d6efd" : "transparent",
        color:
            activeTab === tab ? "#fff" : "#495057",
        fontSize: "13px",
        fontWeight: 600,
        cursor: "pointer",
        transition: "0.2s ease",
        whiteSpace: "nowrap"
    });

    const renderHeading = () => (
        <div className="cpa">
            <i className="fa fa-list me-2" aria-hidden="true" />
            {activeTab === "active"
                ? "Active Cashbooks"
                : "Inactive Cashbooks"}

            <span className="badge bg-orange ms-2">
                {filteredCashbooks.length}
            </span>
        </div>
    );

    const renderListToggle = () => (
        <Link
            to="#"
            className={`SlideToolHeader ${listOpen ? "collapse" : "expand"
                }`}
            onClick={(event) => {
                event.preventDefault();
                setListOpen((previous) => !previous);
            }}
            aria-label="Toggle cashbook list"
            aria-expanded={listOpen}
        >
            <i className="fas fa-angle-up" aria-hidden="true" />
        </Link>
    );

    return (
        <>
            <CashPassbookEntriesModal
                show={Boolean(viewPassbook)}
                storeId={viewPassbook?.storeId || ""}
                passbookId={viewPassbook?.passbookId || ""}
                handleClose={() => setViewPassbook(null)}
            />
            <PassbookDetailsModal
                passbook={selectedPassbook}
                handleClose={() => setSelectedPassbook(null)}
            />
            <CashbookAddModal
                show={showAddModal}
                handleClose={() => setShowAddModal(false)}
                onCreated={handleCreated}
            />

            <div className="row">
                <div className="col-xl-12">
                    {/* Filter panel */}
                    <div className="filter cm-content-box box-primary">
                        <div className="content-title">
                            <div className="cpa">
                                <i className="fas fa-filter me-2" />
                                Filter
                            </div>

                            <div className="tools">
                                <Link
                                    to="#"
                                    className={`SlideToolHeader ${filterOpen ? "collapse" : "expand"
                                        }`}
                                    onClick={(event) => {
                                        event.preventDefault();
                                        setFilterOpen((previous) => !previous);
                                    }}
                                    aria-label="Toggle filters"
                                    aria-expanded={filterOpen}
                                >
                                    <i className="fas fa-angle-up" />
                                </Link>
                            </div>
                        </div>

                        <Collapse in={filterOpen}>
                            <div className="cm-content-body form excerpt">
                                <div className="card-body">
                                    <div className="row g-2 align-items-center">
                                        {/* Search */}
                                        <div className="col-12 col-md-6 col-xl-4">
                                            <input
                                                type="text"
                                                className="form-control"
                                                placeholder="Search passbook, employee, mobile, store or remark..."
                                                value={searchTerm}
                                                onChange={(event) => setSearchTerm(event.target.value)}
                                                aria-label="Search cashbooks"
                                            />
                                        </div>

                                        {/* Stores with existing passbooks only */}
                                        <div className="col-12 col-md-6 col-xl-3">
                                            <Select
                                                inputId="cashbook-store-filter"
                                                options={storeOptions}
                                                value={
                                                    storeOptions.find(
                                                        (option) => option.value === String(storeFilter || "")
                                                    ) || null
                                                }
                                                onChange={(selected) => {
                                                    setStoreFilter(selected?.value || "");
                                                    setPage(0);
                                                }}
                                                isSearchable
                                                isClearable
                                                isLoading={loading}
                                                isDisabled={loading}
                                                className="custom-react-select"
                                                placeholder="All Stores with Passbooks"
                                                aria-label="Filter by store"
                                                noOptionsMessage={() => "No matching stores with passbooks"}
                                                menuPlacement="auto"
                                                menuPortalTarget={
                                                    typeof document !== "undefined" ? document.body : undefined
                                                }
                                                styles={{
                                                    menuPortal: (base) => ({
                                                        ...base,
                                                        zIndex: 9999
                                                    })
                                                }}
                                                formatOptionLabel={(option, { context }) => (
                                                    <div className="d-flex justify-content-between align-items-center gap-2">
                                                        <span style={{ overflowWrap: "anywhere" }}>
                                                            {option.label}
                                                        </span>

                                                        {context === "menu" && option.value && (
                                                            <span className="badge bg-secondary flex-shrink-0">
                                                                {option.count}
                                                            </span>
                                                        )}
                                                    </div>
                                                )}
                                            />
                                        </div>

                                        {/* Balance */}
                                        <div className="col-12 col-md-6 col-xl-3">
                                            <Select
                                                options={balanceOptions}
                                                value={
                                                    balanceOptions.find(
                                                        (option) => option.value === balanceFilter
                                                    ) || null
                                                }
                                                onChange={(selected) =>
                                                    setBalanceFilter(selected?.value || "")
                                                }
                                                isSearchable={false}
                                                className="custom-react-select"
                                                placeholder="All Balances"
                                                aria-label="Filter by balance"
                                            />
                                        </div>

                                        {/* Rows and Reset */}
                                        <div className="col-12 col-md-6 col-xl-2">
                                            <div className="d-flex align-items-center gap-2">
                                                <div style={{ flex: "1 1 0", minWidth: 0 }}>
                                                    <Select
                                                        options={rowOptions}
                                                        value={
                                                            rowOptions.find(
                                                                (option) => option.value === pageSize
                                                            ) || null
                                                        }
                                                        onChange={(selected) =>
                                                            setPageSize(selected?.value || 100)
                                                        }
                                                        isSearchable={false}
                                                        className="custom-react-select"
                                                        placeholder="Rows to display"
                                                        aria-label="Rows to display"
                                                    />
                                                </div>

                                                {Boolean(searchTerm || storeFilter || balanceFilter) && (
                                                    <button
                                                        type="button"
                                                        className="btn btn-outline-danger flex-shrink-0"
                                                        onClick={() => {
                                                            setSearchTerm("");
                                                            setStoreFilter("");
                                                            setBalanceFilter("");
                                                            setPage(0);
                                                        }}
                                                        title="Reset filters"
                                                        aria-label="Reset all filters"
                                                        style={{
                                                            padding: "6px 10px",
                                                            fontSize: "20px",
                                                            lineHeight: 1
                                                        }}
                                                    >
                                                        ×
                                                    </button>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </Collapse>
                    </div>

                    {/* Cashbook list panel */}
                    <div className="filter cm-content-box box-primary">
                        <div className="content-title d-flex flex-column flex-md-row justify-content-between align-items-center gap-3">
                            {/* Mobile heading */}
                            <div className="d-flex justify-content-between align-items-center w-100 d-md-none">
                                {renderHeading()}

                                <div className="tools">
                                    {renderListToggle()}
                                </div>
                            </div>

                            {/* Desktop heading */}
                            <div className="text-center text-md-start d-none d-md-block">
                                {renderHeading()}
                            </div>

                            {/* Center tabs */}
                            <div
                                className="d-flex align-items-center flex-nowrap order-2 order-md-0 align-self-start"
                                style={{
                                    background: "#f1f3f5",
                                    padding: "5px",
                                    borderRadius: "12px",
                                    border: "1px solid #dee2e6",
                                    maxWidth: "100%",
                                    overflowX: "auto"
                                }}
                            >
                                <button
                                    type="button"
                                    style={tabStyle("active")}
                                    onClick={() => setActiveTab("active")}
                                >
                                    Active Cashbooks
                                </button>

                                <button
                                    type="button"
                                    className="ms-2"
                                    style={tabStyle("inactive")}
                                    onClick={() => setActiveTab("inactive")}
                                >
                                    Inactive Cashbooks
                                </button>
                            </div>

                            {/* Desktop collapse control */}
                            <div className="tools d-none d-md-flex align-items-center justify-content-center">
                                {renderListToggle()}
                            </div>
                        </div>

                        {/* Single Add button */}
                        <div className="row me-0 me-md-3">
                            <div className="col-12 d-flex w-100 justify-content-start justify-content-md-end ms-3 ms-md-0 me-md-4 mb-2">
                                <button
                                    type="button"
                                    className="btn btn-primary btn-sm d-flex align-items-center"
                                    onClick={() => setShowAddModal(true)}
                                >
                                    <i className="fas fa-plus me-2" />
                                    Add New Cashbook
                                </button>
                            </div>
                        </div>



                        <Collapse in={listOpen}>
                            <div className="cm-content-body form excerpt">
                                <div className="card-body">

                                    <div className="table-responsive">
                                        <div className="dataTables_wrapper no-footer">
                                            <div className="scrolltable">
                                                <table
                                                    className="table table-bordered table-responsive-lg table-striped table-condensed flip-content cashbook-data-table"
                                                    id="cashbooks-data"
                                                >
                                                    <thead className="sticky-table-header bg-white">
                                                        <tr>
                                                            <th>S.No</th>
                                                            <th className="text-center text-nowrap">View</th>
                                                            <th>Details</th>
                                                            <th>Store</th>
                                                            <th>Location</th>
                                                            <th className="text-end text-nowrap">Total In</th>
                                                            <th className="text-end text-nowrap">Total Out</th>
                                                            <th className="text-end text-nowrap">Balance</th>
                                                            <th className="text-center">Status</th>
                                                        </tr>
                                                    </thead>

                                                    <tbody>
                                                        {loading ? (
                                                            <tr>
                                                                <td colSpan={9} className="text-center py-4">
                                                                    <span
                                                                        className="spinner-border spinner-border-sm me-2"
                                                                        aria-hidden="true"
                                                                    />
                                                                    Loading cashbooks...
                                                                </td>
                                                            </tr>
                                                        ) : visibleCashbooks.length === 0 ? (
                                                            <tr>
                                                                <td colSpan={9} className="text-center">
                                                                    {error
                                                                        ? "Cashbooks could not be loaded. Please refresh and try again."
                                                                        : `No ${activeTab} cashbooks found`}
                                                                </td>
                                                            </tr>
                                                        ) : (
                                                            visibleCashbooks.map((cashbook, index) => (
                                                                <tr key={cashbook._id}>
                                                                    <td>{start + index + 1}</td>

                                                                    {/* Open fullscreen passbook transactions */}
                                                                    <td className="text-center">
                                                                        <button
                                                                            type="button"
                                                                            className="btn btn-sm btn-outline-primary"
                                                                            disabled={loading || !cashbook.store}
                                                                            title="View passbook transactions"
                                                                            aria-label={`View transactions for ${cashbook.name || "passbook"}`}
                                                                            onClick={() => {
                                                                                const storeId = cashbook.store?._id || cashbook.store;

                                                                                if (!storeId) return;

                                                                                setViewPassbook({
                                                                                    storeId: String(storeId),
                                                                                    passbookId: String(cashbook._id)
                                                                                });
                                                                            }}
                                                                            style={{
                                                                                padding: "4px 9px",
                                                                                borderRadius: "6px",
                                                                                boxShadow: "0 2px 5px rgba(0,0,0,0.10)"
                                                                            }}
                                                                        >
                                                                            <i className="fa fa-eye" aria-hidden="true" />
                                                                        </button>
                                                                    </td>

                                                                    {/* Click to open Passbook Details */}
                                                                    <td
                                                                        onClick={() => setSelectedPassbook(cashbook)}
                                                                        style={{ cursor: "pointer" }}
                                                                        title="Click to view passbook details"
                                                                    >
                                                                        <div
                                                                            style={{
                                                                                minWidth: "220px",
                                                                                lineHeight: 1.5,
                                                                                overflowWrap: "anywhere"
                                                                            }}
                                                                        >
                                                                            <button
                                                                                type="button"
                                                                                onClick={(event) => {
                                                                                    event.stopPropagation();
                                                                                    setSelectedPassbook(cashbook);
                                                                                }}
                                                                                style={{
                                                                                    padding: 0,
                                                                                    border: 0,
                                                                                    background: "transparent",
                                                                                    color: "#1565c0",
                                                                                    font: "inherit",
                                                                                    fontWeight: 600,
                                                                                    textAlign: "left",
                                                                                    cursor: "pointer"
                                                                                }}
                                                                                aria-label={`View details for ${cashbook.name || "passbook"}`}
                                                                            >
                                                                                {cashbook.name || "—"}
                                                                            </button>

                                                                            <div>
                                                                                <strong>CashMaker: </strong>
                                                                                {cashbook.employee?.name || "—"}
                                                                            </div>

                                                                            <div>
                                                                                <strong>Email: </strong>
                                                                                {cashbook.employee?.email || "—"}
                                                                            </div>

                                                                            <div>
                                                                                <strong>Mobile: </strong>
                                                                                {cashbook.employee?.mobileNo || "—"}
                                                                            </div>
                                                                        </div>
                                                                    </td>

                                                                    {/* Store */}
                                                                    <td className="scrollable-description">
                                                                        {cashbook.store?.name || "-"}
                                                                    </td>

                                                                    {/* Location */}
                                                                    <td>{cashbook.store?.location || "-"}</td>

                                                                    {/* Total In */}
                                                                    <td className="text-end text-nowrap">
                                                                        {formatCashAmount(cashbook.totalIn)}
                                                                    </td>

                                                                    {/* Total Out */}
                                                                    <td className="text-end text-nowrap">
                                                                        {formatCashAmount(cashbook.totalOut)}
                                                                    </td>

                                                                    {/* Balance */}
                                                                    <td className="text-end text-nowrap">
                                                                        {formatCashAmount(cashbook.balance)}
                                                                    </td>



                                                                    {/* Status toggle */}
                                                                    <td className="text-center">
                                                                        <button
                                                                            type="button"
                                                                            role="switch"
                                                                            aria-checked={cashbook.status === "active"}
                                                                            aria-label={`${cashbook.status === "active" ? "Deactivate" : "Activate"
                                                                                } passbook ${cashbook.name || ""}`}
                                                                            title={
                                                                                cashbook.status === "active"
                                                                                    ? "Active — click to deactivate"
                                                                                    : "Inactive — click to activate"
                                                                            }
                                                                            disabled={Boolean(changingId) || loading}
                                                                            onClick={() => toggleStatus(cashbook)}
                                                                            style={{
                                                                                display: "inline-flex",
                                                                                alignItems: "center",
                                                                                justifyContent:
                                                                                    cashbook.status === "active" ? "flex-end" : "flex-start",
                                                                                width: "40px",
                                                                                height: "22px",
                                                                                padding: "3px",
                                                                                border: 0,
                                                                                borderRadius: "20px",
                                                                                background:
                                                                                    cashbook.status === "active" ? "#198754" : "#6c757d",
                                                                                boxShadow: "0 2px 5px rgba(0,0,0,0.18)",
                                                                                cursor:
                                                                                    changingId || loading ? "not-allowed" : "pointer",
                                                                                opacity: changingId || loading ? 0.6 : 1
                                                                            }}
                                                                        >
                                                                            <span
                                                                                aria-hidden="true"
                                                                                style={{
                                                                                    display: "block",
                                                                                    width: "16px",
                                                                                    height: "16px",
                                                                                    borderRadius: "50%",
                                                                                    background: "#ffffff",
                                                                                    boxShadow: "0 1px 3px rgba(0,0,0,0.25)"
                                                                                }}
                                                                            />
                                                                        </button>

                                                                        {changingId === cashbook._id && (
                                                                            <div className="small text-muted mt-1" role="status">
                                                                                Updating...
                                                                            </div>
                                                                        )}
                                                                    </td>
                                                                </tr>
                                                            ))
                                                        )}
                                                    </tbody>
                                                </table>
                                            </div>

                                            {/* Same shared pagination as Location */}
                                            {!loading && (
                                                <div className="d-sm-flex text-center justify-content-between align-items-center mt-3">
                                                    <div className="dataTables_info">
                                                        Showing{" "}
                                                        {filteredCashbooks.length > 0
                                                            ? start + 1
                                                            : 0}
                                                        {" "}to{" "}
                                                        {Math.min(
                                                            filteredCashbooks.length,
                                                            start + pageSize
                                                        )}
                                                        {" "}of {filteredCashbooks.length} entries
                                                    </div>

                                                    <Pagination
                                                        activePage={currentPage}
                                                        totalPages={totalPages}
                                                        onPageChange={handlePageChange}
                                                    />
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </Collapse>
                    </div>
                </div>
            </div>
        </>
    );
};

export default CashbookManagement;